import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import styles from "./Charts.module.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface BarChartProps {
  title: string;
  data: Array<{ label: string; value: number }>;
  horizontal?: boolean;
  color?: string;
  currency?: string;
  backgroundColor?: string;
  hoverColor?: string;
  showDecimals?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
  title,
  data,
  horizontal = false,
  color = "#4A90E2",
  currency,
  backgroundColor,
  hoverColor,
  showDecimals = true,
}) => {
  const chartData = {
    labels: data.map((d) => d.label),
    datasets: [
      {
        label: title,
        data: data.map((d) => d.value),
        backgroundColor: backgroundColor
          ? data.map((d, index) => backgroundColor)
          : data.map((d, index) =>
              d.value === Math.max(...data.map((x) => x.value))
                ? "#1E3A5F"
                : color
            ),
        hoverBackgroundColor: hoverColor
          ? data.map((d, index) => hoverColor)
          : undefined,
        borderRadius: 6,
        barThickness: horizontal ? 20 : undefined,
      },
    ],
  };

  const options = {
    indexAxis: horizontal ? ("y" as const) : ("x" as const),
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: function (context: any) {
            // Show the day/label as the title
            return context[0].label || "";
          },
          label: function (context: any) {
            // Show the formatted value
            const value = horizontal ? context.parsed.x : context.parsed.y;
            const formattedValue = showDecimals
              ? value.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : Math.round(value).toLocaleString("en-US");
            return currency ? `${currency} ${formattedValue}` : formattedValue;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "#f0f0f0",
          display: !horizontal,
        },
      },
      x: {
        grid: {
          display: horizontal,
          color: "#f0f0f0",
        },
      },
    },
  };

  return (
    <div className={styles.chartContainer}>
      <h3 className={styles.chartTitle}>{title}</h3>
      <div className={styles.chartWrapper}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export default BarChart;
